import { useState } from 'react';
import type { MenuProps } from 'antd';
import { Button, Dropdown } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { IoFlashOutline } from 'react-icons/io5';
import {
  FiCheckSquare,
  FiChevronDown,
  FiEdit,
  FiSave,
  FiTrash,
  FiX,
  FiXSquare,
} from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient, useIsMutating } from '@tanstack/react-query';
import {
  confirmPublishRegulation,
  deleteDraftRegulation,
  fetchDraftRegulationById,
  requestDeleteRegulation,
  requestPublishRegulation,
} from '../../Draft.services.tsx';
import { MoreInfo } from '../more-info';
import { ConfirmModal } from '../../../../components/confirm-modal';
import { usePermissions } from '../../../../hooks/usePermissions.ts';
import { useAuth } from '../../../../context/AuthContext.tsx';
import { RiShareForwardLine } from 'react-icons/ri';
import { toast } from 'react-toastify';
import type { ApiError } from '../../../../types/App.ts';

const RegulationsActionsBar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showRequestPublication, setShowRequestPublication] = useState(false);
  const [showConfirmPublication, setShowConfirmPublication] = useState(false);
  const [showRejectPublication, setShowRejectPublication] = useState(false);
  const [showConfirmDeletion, setShowConfirmDeletion] = useState(false);
  const [showMoreInfo, setShowMoreInfo] = useState(false);

  const handleShowMoreInfo = () => setShowMoreInfo(true);
  const handleCloseMoreInfo = () => setShowMoreInfo(false);
  const { id } = useParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isSaving = useIsMutating({ mutationKey: ['saveDraftRegulation'] }) > 0;

  const { data } = useQuery({
    queryKey: ['draftRegulation', id],
    queryFn: () => fetchDraftRegulationById(id),
    enabled: !!id,
    retry: false,
  });
  const { isAllowed, isAdmin, isRegularUser } = usePermissions(data?.type);

  const isView = location.pathname.includes('view');

  const isInLeadership = data?.unitInCharge?.leadership?.includes(user?.id);

  const canSaveDraft =
    (!isView && data?.status !== 2 && isRegularUser) ||
    (!isView && data?.status !== 3 && isAdmin) ||
    (!isView && data?.status === 2 && isInLeadership);
  const canCancel = (!isView && isRegularUser) || (!isView && isAdmin);
  const canEditDraft =
    (data?.status === 1 && isView && isRegularUser) ||
    (data?.status === 2 && isView && isAllowed) ||
    (data?.status === 2 && isView && isAdmin);
  const canDeleteDraft =
    (data?.status === 1 && !isView && isRegularUser) ||
    (data?.status === 1 && isView && isRegularUser) ||
    (data?.status === 2 && isView && isRegularUser) ||
    (data?.status === 2 && isView && isAllowed) ||
    (data?.status === 2 && isView && isAdmin);
  const canRequestPublication = id && data?.status === 1 && isView;
  const canPublish =
    (id && data?.status === 2 && isAllowed) ||
    (id && data?.status === 2 && isAdmin) ||
    (id && data?.status === 2 && isInLeadership && data?.type === 3);
  const canDeclinePublish =
    (id && data?.status === 2 && isAllowed) ||
    (id && data?.status === 2 && isAdmin) ||
    (id && data?.status === 2 && isInLeadership && data?.type === 3);

  const deleteMutation = useMutation({
    mutationFn: deleteDraftRegulation,
    onSuccess: () => {
      navigate('/');
      queryClient.invalidateQueries({ queryKey: ['draftRegulation'] });
    },
    onError: () => {
      console.error('Failed to delete draft');
    },
  });

  const handleMenuClick = (item: { key: string }) => {
    switch (item.key) {
      case '1': {
        if (isSaving) break;
        const form = document.getElementById('DraftRegulationForm') as HTMLFormElement | null;
        form?.requestSubmit();
        break;
      }
      case '2': {
        navigate(`/regulation/draft/${id}`);
        break;
      }
      case '3': {
        if (!id) return;
        deleteMutation.mutate(id);
        break;
      }
      case '4': {
        setShowRequestPublication(true);
        break;
      }
      case '5': {
        setShowConfirmPublication(true);
        break;
      }
      case '6': {
        setShowRejectPublication(true);
        break;
      }
      case '7': {
        const recipients = Array.isArray(data.forwardEmails)
          ? data.forwardEmails.join(',')
          : String(data.forwardEmails);

        const subject = encodeURIComponent(data.title || 'Regulation Draft');
        const url = `${window.location.origin}/regulation/draft/view/${data.id}`;
        const body = encodeURIComponent(`Link to the regulation:\n${url}`);
        window.location.href = `mailto:${recipients}?subject=${subject}&body=${body}`;
        break;
      }
      case '8': {
        navigate(-1);
        break;
      }
      default: {
        break;
      }
    }
  };
  const items: MenuProps['items'] = [
    ...(canSaveDraft
      ? [
          {
            label: t('common.save'),
            key: '1',
            icon: <FiSave />,
            disabled: isSaving,
          },
        ]
      : []),
    ...(canEditDraft
      ? [
          {
            label: t('common.edit'),
            key: '2',
            icon: <FiEdit />,
          },
        ]
      : []),
    ...(canDeleteDraft
      ? [
          {
            label: t('common.delete-draft'),
            key: '3',
            icon: <FiTrash />,
          },
        ]
      : []),
    ...(canRequestPublication
      ? [
          {
            label: t('common.ready-approval'),
            key: '4',
            icon: <FiCheckSquare />,
          },
        ]
      : []),
    ...(canPublish
      ? [
          {
            label: t('common.confirm-regulation'),
            key: '5',
            icon: <FiCheckSquare />,
          },
        ]
      : []),
    ...(canDeclinePublish
      ? [
          {
            label: t('common.reject-regulation'),
            key: '6',
            icon: <FiXSquare />,
          },
        ]
      : []),
    ...(data?.forwardEmails && isView && id
      ? [
          {
            label: t('common.forward'),
            key: '7',
            icon: <RiShareForwardLine />,
          },
        ]
      : []),
    ...(canCancel
      ? [
          {
            label: t('common.cancel'),
            key: '8',
            icon: <FiX />,
          },
        ]
      : []),
  ];

  const requestPublishMutation = useMutation({
    mutationKey: ['publishDraftRegulation'],
    mutationFn: requestPublishRegulation,
    onSuccess: () => {
      navigate('/');
      queryClient.invalidateQueries({ queryKey: ['draftRegulations'] });
    },
    onError: (error: ApiError) => {
      toast.error(error?.response?.data?.message || error.message);
    },
  });

  const confirmPublishMutation = useMutation({
    mutationKey: ['publishDraftRegulation'],
    mutationFn: confirmPublishRegulation,
    onSuccess: () => {
      navigate('/');
      queryClient.invalidateQueries({ queryKey: ['draftRegulations'] });
    },
    onError: (error: ApiError) => {
      toast.error(error?.response?.data?.message || error.message);
    },
  });

  const requestDeleteMutation = useMutation({
    mutationFn: requestDeleteRegulation,
    onSuccess: () => {
      navigate('/');
      queryClient.invalidateQueries({ queryKey: ['draftRegulations'] });
    },
    onError: () => {
      console.error('Failed to request del;etion');
    },
  });

  const handleCloseRequestPublication = () => setShowRequestPublication(false);
  const handleCloseConfirmPublication = () => setShowConfirmPublication(false);
  const handleCloseConfirmDeletion = () => setShowConfirmDeletion(false);

  const handleRequestPublication = (message: string) => {
    if (!id) return;
    requestPublishMutation.mutate({ id, data: { requestMessage: message } });
    setShowRequestPublication(false);
  };

  const handleConfirmPublication = (
    message: string,
    subject: string,
    groups: number[],
    notifications: boolean,
    isBodyHtml: boolean,
  ) => {
    if (!id) return;
    confirmPublishMutation.mutate({
      id,
      data: {
        requestMessage: message,
        title: notifications ? subject : null,
        distributionLists: notifications ? groups.map((item) => Number(item)) : null,
        isBodyHtml: isBodyHtml,
      },
    });
    setShowConfirmPublication(false);
  };

  const handleConfirmDeletion = (message: string) => {
    requestDeleteMutation.mutate({ id, data: { declinedMessage: message } });
    setShowConfirmDeletion(false);
  };

  const menuProps = {
    items,
    onClick: handleMenuClick,
  };
  return (
    <>
      <div className="action-bar-container">
        <div className="action-bar-left">
          <Button
            size="large"
            onClick={handleShowMoreInfo}
            icon={<IoFlashOutline />}
            iconPosition="start"
          >
            {t('common.more')}
            <FiChevronDown />
          </Button>
        </div>
        <div className="action-bar-right">
          <Dropdown menu={menuProps}>
            <Button size="large" type="primary" icon={<IoFlashOutline />} iconPosition="start">
              Actions
              <FiChevronDown />
            </Button>
          </Dropdown>
        </div>
      </div>
      {showMoreInfo && <MoreInfo open={showMoreInfo} onClose={handleCloseMoreInfo} />}
      {showRequestPublication && (
        <ConfirmModal
          simple
          open={showRequestPublication}
          title={t('modals.confirm-request-publication')}
          message={t('modals.confirm-request-publication-message')}
          onClose={handleCloseRequestPublication}
          onConfirm={handleRequestPublication}
        />
      )}
      {showConfirmPublication && (
        <ConfirmModal
          open={showConfirmPublication}
          title={t('modals.confirm-approve-publication')}
          message={t('modals.confirm-approve-publication-message')}
          subject={data?.title}
          onClose={handleCloseConfirmPublication}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          onConfirm={handleConfirmPublication}
        />
      )}
      {showConfirmDeletion && (
        <ConfirmModal
          open={showConfirmDeletion}
          title={t('modals.confirm-request-deletion')}
          message={t('modals.confirm-request-deletion-message')}
          simple
          onClose={handleCloseConfirmDeletion}
          onConfirm={handleConfirmDeletion}
        />
      )}
      {showRejectPublication && (
        <ConfirmModal
          open={showRejectPublication}
          title={t('modals.confirm-request-deletion')}
          message={t('modals.confirm-request-deletion-message')}
          simple
          onClose={handleCloseConfirmDeletion}
          onConfirm={handleConfirmDeletion}
        />
      )}
    </>
  );
};

export default RegulationsActionsBar;